import {
  DragEndEvent,
  DragOverlay,
  useDndMonitor,
  useDraggable,
} from "@dnd-kit/core"
import React, { useEffect, useState } from "react"
import ReactDOM from "react-dom"
import { useParams } from "react-router-dom"
import {
  RootState,
  useTypedDispatch,
  useTypedSelector,
} from "../app/store"
import { DroppableArea } from "../features/simulation-decisions/components/droppable-area"
import Message from "../features/simulation-messages/components/message"
import {
  MessageTypeIdentifiers,
  MessageTypes,
} from "../features/simulation-messages/types"
import {
  addMessage,
  simulationSlice,
} from "../features/simulation/simulationSlice"
import { Event, SavePointLogType } from "../features/simulation/types"
import {
  convertToSeconds,
  convertToTime,
  getDate,
  getMessageType,
  getSimRunId,
} from "../features/simulation/utils"
import { removeAlert } from "../features/user-alerts/userAlertsSlice"

type DraggableMessagesProps = {
  id: string
  children: React.ReactNode
}

const DraggableMessage = ({ id, children }: DraggableMessagesProps) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id,
  })

  return (
    <div
      id={id}
      ref={setNodeRef}
      className="cursor-grabbing self-stretch"
      {...listeners}
      {...attributes}>
      {children}
    </div>
  )
}

const findMessageFromDraggable = (id: number, messages: Event[]) => {
  if (id == null) {
    console.error("findMessageFromDraggable called with null/undefined id")
    return null
  }
  const message = messages.find(
    (message) => message.id?.toString() === id.toString(),
  )
  if (!message) {
    console.error("message not found " + id)
    return null
  }
  return message
}

const eventToMessage = (event: Event) => {
  const message = event.description || ""
  const time = event.elapsedTime ?? ""
  const messageType = getMessageType(event)
  const connections = event.connections ?? []

  // Create a more unique key to prevent duplicates
  const uniqueKey = `message-${event.id}-${event.eventId || 'no-event-id'}-${time.replace(/:/g, '')}`

  // Ensure event.id exists before creating draggable message
  if (event.id == null) {
    return (
      <Message
        time={time}
        message={message}
        messageType={messageType}
        connections={connections}
      />
    )
  }

  return (
    <DraggableMessage id={event.id.toString()} key={uniqueKey}>
      <Message
        time={time}
        message={message}
        messageType={messageType}
        connections={connections}
      />
    </DraggableMessage>
  )
}

const showOverlay = (activeEvent: Event) => {
  const message = activeEvent?.description || ""
  const time: string = activeEvent?.elapsedTime || ""
  const messageType = activeEvent
    ? getMessageType(activeEvent)
    : MessageTypes.INCOMING
  const dragRoot = document.getElementById("drag-root")
  if (!dragRoot) {
    return
  }

  const overlay = (
    <DragOverlay dropAnimation={null}>
      <div
        className="bg-white border-4 border-solid border-blue-500 rounded-xl shadow-lg w-4/5 z-50 p-2 opacity-90 cursor-grabbing">
        <Message time={time} message={message} messageType={messageType} />
      </div>
    </DragOverlay>
  )

  return ReactDOM.createPortal(overlay, dragRoot)
}

const MessagesWithSearch: React.FC = () => {
  const dispatch = useTypedDispatch()

  const [searchTerm, setSearchTerm] = useState("")
  const [filteredMessages, setFilteredMessages] = useState<Event[]>([])
  const droppableID = "message-droppable-messages"
  const [activeMessage, setActiveMessage] = useState<Event | null>(null)
  const [lastOldestMessage, setLastOldestMessage] = React.useState<Element>()
  const params = useParams()

  const simulationStore = useTypedSelector((state: RootState) => {
    return state.simulationSlice
  })
  const { alerts: userAlerts, count: userAlertsCount } = useTypedSelector(
    (state: RootState) => state.userAlertsSlice,
  )

  const messagesAndAlerts = (): SavePointLogType[] => {
    const runId = getSimRunId(params)
    
    // Handle demo mode or missing runId
    if (!runId || runId === 0 || !userAlerts[runId]) {
      return simulationStore.messages
    }
    
    // Get alerts, but remove ones where there is no corresponding message
    const alerts = Object.values(userAlerts[runId]).filter((alert) =>
      simulationStore.messages.some(
        (message) => message.id?.toString() === alert.eventId?.toString(),
      ),
    )
    const alertsMissingMessage = alerts.filter(
      (alert) =>
        !simulationStore.messages.some(
          (message) => message.id?.toString() === alert.eventId?.toString(),
        ),
    )
    for (const alert of alertsMissingMessage) {
      // remove alerts that have no corresponding message from redux
      dispatch(removeAlert({ runId, alert }))
    }
    
    // Create a map to track used IDs more efficiently
    const usedIds = new Set<number>()
    
    // Process messages first
    const processedMessages = simulationStore.messages.map(msg => {
      usedIds.add(msg.id)
      return msg
    })
    
    // Process alerts with unique IDs, ensuring no conflicts
    let maxId = processedMessages.length > 0 ? Math.max(...processedMessages.map(m => m.id)) : 0
    const processedAlerts = alerts.map(alert => {
      let newId = alert.id
      // If the alert ID conflicts with existing message IDs, assign a new unique ID
      if (usedIds.has(newId)) {
        newId = ++maxId
      }
      usedIds.add(newId)
      return { ...alert, id: newId }
    })
    
    const allMessages = [...processedMessages, ...processedAlerts]
    // sort by id
    return allMessages.sort((a, b) => a.id - b.id)
  }

  useEffect(() => {
    let nextEvent = null
    if (simulationStore.nextEvent == null) {
      if (simulationStore.events?.length > 0) {
        nextEvent = simulationStore.events[0]
        dispatch(simulationSlice.actions.setNextEvent(nextEvent))
      } else {
        return
      }
    } else {
      nextEvent = simulationStore.nextEvent
    }
    const eventTime = convertToSeconds(nextEvent.elapsedTime ?? "")
    if (simulationStore.secondsElapsed <= eventTime) {
      return
    } else {
      console.debug(
        `event time reached at ${eventTime} / ${simulationStore.secondsElapsed} elapsed`,
      )

      const eventsToProcess = [nextEvent]

      // Take a moment to check to see which other events in the queue are ready to be processed
      for (const event of simulationStore.events) {
        if (
          convertToSeconds(event.elapsedTime ?? "") <=
            simulationStore.secondsElapsed &&
          event.id
        ) {
          const eventIdToAdd = event.eventId

          // Check if the eventIdToAdd is not already present in eventsToProcess
          if (!eventsToProcess.some((item) => item.eventId === eventIdToAdd)) {
            eventsToProcess.push(event)
          }
        }
      }
      console.debug("events to process", eventsToProcess.length)
      const eventsToKeep = simulationStore.events.filter(
        (event) =>
          convertToSeconds(event.elapsedTime ?? "") >
          simulationStore.secondsElapsed,
      )
      console.debug("events remaining", eventsToKeep.length)
      // Updating the unique id for messages
      const updatedEvent = {
        ...eventsToProcess[0],
        id: 0, // This will be set properly when added to messages
      }
      if (eventsToProcess.length == 1) {
        dispatch(simulationSlice.actions.setLoadingMessages(true))
      }
      eventsToProcess[0] = updatedEvent
      // Update the store
      const pastEvents = [...simulationStore.pastEvents].concat(eventsToProcess)
      
      // Ensure unique IDs for all messages
      let nextId = simulationStore.messages.length > 0 
        ? Math.max(...simulationStore.messages.map(msg => msg.id)) + 1 
        : 1

      const allMessages =
        simulationStore.messages && simulationStore.messages.length > 0
          ? [
              ...simulationStore.messages,
              ...eventsToProcess
                .filter(
                  (event) =>
                    !simulationStore.messages.some(
                      (msg) => msg.eventId === event.eventId,
                    ),
                )
                .map((event) => {
                  const newMessage = {
                    ...event,
                    id: nextId++, // Use incrementing unique ID
                  }
                  return newMessage
                }),
            ]
          : eventsToProcess.map((event) => ({
              ...event,
              id: nextId++, // Start from 1 and increment
            }))

      dispatch(simulationSlice.actions.setEvents(eventsToKeep))
      dispatch(simulationSlice.actions.setPastEvents(pastEvents))
      dispatch(simulationSlice.actions.setMessages(allMessages))
      dispatch(simulationSlice.actions.setNextEvent(eventsToKeep[0]))
    }
  }, [simulationStore.secondsElapsed])

  useEffect(() => {
    const newFilteredMessages = messagesAndAlerts().filter(
      (event: SavePointLogType): event is Event => {
        return (
          typeof event === "object" &&
          "description" in event &&
          typeof (event as Event).description === "string"
        )
      },
    )
    setFilteredMessages(newFilteredMessages ?? [])
  }, [simulationStore.messages?.length, userAlertsCount])

  useEffect(() => {
    // scroll messages to bottom only if we're not in the configuration view
    const lastMessageList = document.querySelector("#messageList")
    const lastMessage = lastMessageList?.lastElementChild
    if (lastMessage === lastOldestMessage) return

    // Check if the config modal is visible. If so, don't auto-scroll.
    const configPanel = document.querySelector("#aboutSimulationModal");

    if (configPanel) {
        setLastOldestMessage(lastMessage as Element);
        return;
    }

    lastMessage?.scrollIntoView({ behavior: "smooth" })
    setLastOldestMessage(lastMessage as Element)
  }, [filteredMessages, lastOldestMessage])

  const handleInputChange = (e: { target: { value: string } }) => {
    const newSearchTerm = e.target.value
    setSearchTerm(newSearchTerm)
    // Filtering only when the length of the search input exceeds 3 characters
    if (newSearchTerm.length >= 3) {
      const filteredResults = (messagesAndAlerts() as Event[]).filter((event) =>
        event.description?.toLowerCase().includes(newSearchTerm.toLowerCase()),
      )
      if (filteredResults.length === 0) {
        setFilteredMessages([
          {
            id: 0,
            description: "No results found",
            elapsedTime: "",
            dateTime: "",
            type: "incoming",
          },
        ] as Event[])
      } else {
        setFilteredMessages(filteredResults)
      }
    } else {
      setFilteredMessages(messagesAndAlerts() as Event[])
    }
  }

  useDndMonitor({
    onDragStart({ active }) {
      if (active?.id != null) {
        console.debug(`Dragging ${active.id}`)
        setActiveMessage(
          findMessageFromDraggable(
            active.id as number,
            simulationStore.messages,
          ),
        )
      }
    },
    onDragEnd({ active, over }: DragEndEvent) {
      if (over && over.id === droppableID && active?.id != null) {
        console.debug(`Dropped ${active.id} into the droppable area`)
        // lookup message in store
        const message = { ...activeMessage } as Event
        setActiveMessage(null)

        if (!message) {
          console.error("message not found " + active.id)
          return
        }

        const title = message.title || "Message"
        const titleValidation = `${title} validation`

        // Event for validate message
        const event: Event = {
          id: 0, // This will be overridden by addMessage action
          elapsedTime: convertToTime(simulationStore.secondsElapsed),
          date: getDate(),
          description: message.validateResponse || "",
          title: titleValidation,
          type: MessageTypeIdentifiers[MessageTypes.FACT_CHECK],
          eventId: message.eventId,
          mode: "fact_check",
          response: message.validateResponse || "",
          parentMessage: message.title || "",
          categoryId: null,
          planId: null,
          reasonIds: null,
          checked: false,
        }
        console.debug("event", event)

        if (event.description === "") {
          console.error("message description is empty")
          return
        }

        dispatch(addMessage(event))
      }
    },
  })

  return (
    <div
      id="messagesWithSearch"
      className="h-full flex flex-col min-h-0 flex-1 min-w-0">
      {/* Search bar - fixed at top */}
      <div className="flex-shrink-0 bg-base-white py-4 px-6 border-b border-gray-100">
        <div
          id="messageSearchInput"
          className="w-full relative rounded-lg bg-base-white shadow-sm box-border overflow-hidden flex flex-row items-center justify-start py-2.5 px-3.5 text-left text-base text-gray-500 font-text-md-regular border-[1px] border-solid border-gray-300">
          <img
            className="relative w-5 h-5 overflow-hidden shrink-0"
            alt=""
            src="/v3/assets/search-lg.svg"
          />
          <input
            className="w-full bg-transparent outline-none placeholder-gray-500 text-gray-700 pl-2.5 pr-3.5 leading-[24px] font-text-md-regular"
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleInputChange}
          />
          {searchTerm && (
            <img
              className="relative w-4 h-4 overflow-hidden shrink-0 cursor-pointer"
              alt=""
              src="/v3/assets/circle-xmark.svg"
              onClick={() => setSearchTerm("")}
            />
          )}
        </div>
      </div>
      {/* Messages area - scrollable */}
      <div
        id="messages"
        className="flex-1 overflow-y-auto py-4 pl-6 pr-3 text-sm text-gray-700 min-h-0">
        <div
          id="messageList"
          className="flex flex-col items-start justify-start space-y-6">
          {filteredMessages.map((event) => eventToMessage(event))}
          {activeMessage && showOverlay(activeMessage)}
        </div>
      </div>
      {/* Drop area - fixed at bottom */}
      <div className="flex-shrink-0 px-6 py-4 bg-base-white border-t border-gray-100">
        <DroppableArea
          id={droppableID}
          contentActive={content}
          contentDefault={content}
          contentIsOver={content}
        />
      </div>
    </div>
  )
}

const content = (
  <div>
    Drag and drop <b>Incoming Messages</b> that contain information you would
    like to <b>Fact Check</b> here.
  </div>
)

export default MessagesWithSearch
